import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  UserCircleIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth, useAuthActions } from '@/stores/auth';
import { useTheme } from '@/components/providers/ThemeProvider';
import { cn } from '@/utils/cn';
import { useClickOutsideRef } from '@/hooks/useClickOutside';
import { useFocusTrap } from '@/hooks/useKeyboardNavigation';
import { useWebSocketContext } from '@/components/providers/WebSocketProvider';
import { notificationsService } from '@/services/notifications.service';
import NotificationCenter from './NotificationCenter';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
  className?: string;
}

export default function Header({ onMenuClick, showMenuButton = false, className }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { logout } = useAuthActions();
  const { theme, setTheme } = useTheme();
  const { messages } = useWebSocketContext();
  const queryClient = useQueryClient();

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const profileMenuRef = useRef<HTMLDivElement>(null);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Keep the search bar in sync with the URL on the search results page,
  // since it is the single source of the search term (back/forward, deep links)
  useEffect(() => {
    if (location.pathname === '/search') {
      setSearchQuery(searchParams.get('q') ?? '');
    }
  }, [location.pathname, searchParams]);

  // Focus trap for dropdowns
  const themeFocusRef = useFocusTrap<HTMLDivElement>(showThemeMenu);

  // Close menus when clicking outside
  useClickOutsideRef(profileMenuRef, () => setShowProfileMenu(false));
  useClickOutsideRef(themeMenuRef, () => setShowThemeMenu(false));
  useClickOutsideRef(notificationsRef, () => setShowNotifications(false));

  // Global keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setShowThemeMenu(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Navigate to search page with query
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/search');
    }
  };

  const themeIcons = {
    light: SunIcon,
    dark: MoonIcon,
    system: ComputerDesktopIcon,
  };

  const ThemeIcon = themeIcons[theme];

  // Fetch unread notification count from API
  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => notificationsService.getUnreadCount(),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 20000, // Consider data stale after 20 seconds
    retry: 2, // Retry failed requests twice
  });

  const unreadCount = typeof unreadCountData === 'number' ? unreadCountData : 0;

  // Listen for WebSocket notification events and invalidate queries
  React.useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[0];
      if (latestMessage.type === 'notification') {
        // Invalidate the unread count query to fetch updated count
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      }
    }
  }, [messages, queryClient]);

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: {
        duration: 0.1,
      },
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.15,
        ease: 'easeOut',
      },
    },
  };


  return (
    <header
      className={cn(
        'bg-white dark:bg-secondary-900 border-b border-secondary-200 dark:border-secondary-700',
        className
      )}
    >
      <div className="flex items-center justify-between px-4 py-3 lg:px-6">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          {showMenuButton && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onMenuClick}
              className="lg:hidden focus:ring-2 focus:ring-primary-500"
              aria-label="Open navigation menu"
            >
              <Bars3Icon className="w-5 h-5" />
            </Button>
          )}

          {/* Search */}
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search incidents, people, threats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                className="w-40 sm:w-64 lg:w-80"
                aria-label="Search incidents, people, and threats"
              />
              {/* Search suggestions could go here */}
            </div>
          </form>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-2">
          {/* Theme selector */}
          <div className="relative" ref={themeMenuRef}>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setShowThemeMenu(!showThemeMenu)}
              aria-label="Change theme"
            >
              <ThemeIcon className="w-5 h-5" />
            </Button>

            <AnimatePresence>
              {showThemeMenu && (
                <motion.div
                  ref={themeFocusRef}
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="absolute right-0 mt-2 w-40 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg py-1 z-50"
                  role="menu"
                  aria-label="Theme selection"
                >
                  {(['light', 'dark', 'system'] as const).map((themeOption) => {
                    const IconComponent = themeIcons[themeOption];
                    return (
                      <button
                        key={themeOption}
                        onClick={() => handleThemeChange(themeOption)}
                        className={cn(
                          'w-full px-3 py-2 text-left text-sm flex items-center space-x-2 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                          theme === themeOption && 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-100'
                        )}
                      >
                        <IconComponent className="w-4 h-4" />
                        <span className="capitalize">{themeOption}</span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative"
            aria-label="Notifications"
          >
            <BellIcon className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-danger-600 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>

          {/* User menu */}
          <div className="relative" ref={profileMenuRef}>
            <Button
              variant="ghost"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center space-x-2 px-3 py-2"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <UserCircleIcon className="w-5 h-5 text-primary-600" />
              </div>
              <span className="hidden md:block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                {user?.firstName} {user?.lastName}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-secondary-500" />
            </Button>

            <AnimatePresence>
              {showProfileMenu && (
                <motion.div
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="absolute right-0 mt-2 w-56 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg py-1 z-50"
                >
                  <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      {user?.email}
                    </p>
                  </div>

                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <UserCircleIcon className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>

                  <Link
                    to="/settings"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <CogIcon className="w-4 h-4" />
                    <span>Settings</span>
                  </Link>

                  <hr className="my-1 border-secondary-200 dark:border-secondary-700" />

                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Notification Center */}
      <NotificationCenter
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </header>
  );
}